/* ancona-v5-cart.js — ANCONA APEX V5 cart module (route-emitted on /cart;
   lazy-loaded by snippets/cart-drawer.liquid elsewhere). "Move to Client Book"
   with the V4 cart-save exact-line safety semantics, rewritten lean: identity
   (never positional index) binding via strict cart.js reads; SAVE-FIRST with
   confirmed save before any removal; only proven-simple lines removed (qty 1,
   no properties/plan/components/gift card, variant re-verified) — complex
   lines saved and KEPT with honest announcements; pre-mutation re-auth of the
   exact key; section render from the mutation response; independent removal
   confirmation. Dawn stays the commerce authority. */
(() => {
  'use strict';
  if (window.__anconaV5Cart) return;
  window.__anconaV5Cart = true;

  const BOOK = 'ancona:v2:client-book';

  /* Customer-facing strings; localize via window.AnconaV5CartStrings. */
  const S = Object.assign({
    move: 'Move to Client Book',
    movePrefix: 'Move to Client Book: ',
    moved: 'Moved to your Client Book.',
    movedReview: 'Saved to your Client Book. The updated cart could not be re-checked — please review your cart.',
    keptComplex: 'Saved to your Client Book. The cart line was kept: its quantity, personalization, gift details, purchase plan, or bundle contents cannot be recreated automatically.',
    keptUnverified: 'Saved to your Client Book. The cart line was kept: its exact option could not be confirmed as available to re-add.',
    keptChanged: 'Saved to your Client Book. Your cart changed before removal, so the line was kept.',
    busy: 'Your cart is already updating. Please try again in a moment.',
    noIdentity: 'Could not verify this exact option. No removal request was sent.',
    saveFailed: 'Saving is unavailable in this browser. No removal request was sent.',
    cartFailed: 'Could not verify this cart line. No removal request was sent.',
    removalUnconfirmed: 'Saved to your Client Book. The removal was not confirmed — please review the refreshed cart.'
  }, (function (raw) {
    /* Reject Shopify "Translation missing" values so a locale gap
       falls back to the English default instead of leaking the marker. */
    var clean = {};
    for (var k in raw) {
      if (typeof raw[k] === 'string' && raw[k] && raw[k].indexOf('Translation missing') !== 0) clean[k] = raw[k];
    }
    return clean;
  })(window.AnconaV5CartStrings || {}));

  const root = window.Shopify?.routes?.root || '/';
  const url = (p) => root.replace(/\/$/, '') + '/' + String(p).replace(/^\//, '');
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const cleanProductId = (value) => {
    const id = String(value || '').trim();
    return /^\d+$/.test(id) ? id : '';
  };
  const policyAllows = (productId) => {
    const policy = window.AnconaV5?.presentationPolicy;
    return !!(policy && typeof policy.allows === 'function' && policy.allows(cleanProductId(productId)));
  };

  let live = null;
  const say = (msg) => {
    try {
      if (!live) {
        live = document.createElement('div');
        live.setAttribute('aria-live', 'polite');
        live.setAttribute('role', 'status');
        live.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;';
        document.body.appendChild(live);
      }
      live.textContent = '';
      setTimeout(() => { live.textContent = msg; }, 30);
    } catch (e) {}
  };

  /* ---- Client Book save (always confirmed before any removal) ---- */
  const store = () => {
    try { const s = window.localStorage; s.setItem('ancona:v5:t', '1'); s.removeItem('ancona:v5:t'); return s; } catch (e) { return null; }
  };

  const entryFor = (it) => {
    const vid = String(it.variant_id || '').replace(/\D/g, '');
    let img = it.featured_image?.url || it.image || '';
    if (img.startsWith('//')) img = 'https:' + img;
    if (img) img += (img.includes('?') ? '&' : '?') + 'width=128';
    return { h: it.handle, pid: cleanProductId(it.product_id), vid, t: String(it.product_title || it.title || '').slice(0, 120),
      // Cart totals include quantity/discount context. Leave the saved quote
      // empty until Client Book verifies this exact variant's live unit price.
      u: url('products/' + it.handle) + (vid ? '?variant=' + vid : ''), p: '', i: img, ts: Date.now() };
  };

  /* Direct write for the window before the Client Book module loads. Removal
     authorization requires an existing v2 store and exact pid + vid readback;
     a handle-only legacy write can never authorize removal. */
  const saveDirect = (e) => {
    if (!policyAllows(e.pid)) return false;
    const s = store();
    if (!s) return false;
    try {
      const raw = s.getItem(BOOK);
      if (raw) {
        const b = JSON.parse(raw);
        if (!b || b.v !== 2 || !Array.isArray(b.saved)) return false;
        if (!b.ids || typeof b.ids !== 'object' || Array.isArray(b.ids)) b.ids = {};
        const existingProductId = cleanProductId(b.ids[e.h]);
        if (existingProductId && existingProductId !== e.pid) {
          b.saved = b.saved.filter((x) => !(x && x.h === e.h));
          b.compare = Array.isArray(b.compare) ? b.compare.filter((x) => {
            const handle = typeof x === 'string' ? x : x?.h;
            return handle !== e.h;
          }) : [];
          b.recent = Array.isArray(b.recent) ? b.recent.filter((x) => !(x && x.h === e.h)) : [];
          delete b.ids[e.h];
        }
        b.ids[e.h] = e.pid;
        b.saved = b.saved.filter((x) => !(x && x.h === e.h && String(x.vid || '') === e.vid));
        b.saved.unshift(e);
        b.saved = b.saved.slice(0, 50);
        s.setItem(BOOK, JSON.stringify(b));
        try { document.dispatchEvent(new CustomEvent('ancona:v5:book-change', { detail: { source: 'cart-move', h: e.h } })); } catch (x) {}
        const c = JSON.parse(s.getItem(BOOK));
        return !!c?.saved?.some?.(
          (x) =>
            x &&
            x.h === e.h &&
            cleanProductId(x.pid || x.productId || c?.ids?.[e.h]) === e.pid &&
            String(x.vid || '') === e.vid
        );
      }
      return false;
    } catch (x) { return false; }
  };

  const saveToBook = async (e) => {
    let api = window.AnconaClientBook;
    if (!api?.savePersistent && window.AnconaV5?.loadClientBook) {
      try { api = await window.AnconaV5.loadClientBook(); } catch (x) {}
    }
    if (api) {
      try {
        if (api.ready) await api.ready();
        if (typeof api.savePersistent === 'function') {
          return !!api.savePersistent(e);
        }
        return saveDirect(e);
      } catch (x) { return saveDirect(e); }
    }
    return saveDirect(e);
  };

  /* ---- Strict cart reads and exact-line authorization ---- */
  const readCart = async () => {
    const r = await fetch(url('/cart.js'), { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
    if (!r.ok) throw new Error('cart-read');
    const c = await r.json();
    if (!c || !Array.isArray(c.items)) throw new Error('cart-read');
    return c;
  };

  const matches = (cart, h, vid) =>
    cart.items.filter((x) => x && String(x.handle) === String(h) && String(x.variant_id) === String(vid));

  const hasComponents = (it) => {
    if (it.parent_relationship || it.parent || it.parent_id || it.parent_key || it.parent_line_key ||
        it.has_components === true || it.requires_components === true || it.bundle_parent === true) return true;
    return ['components', 'line_components', 'item_components', 'component_items', 'bundle_components']
      .some((f) => { const v = it[f]; return Array.isArray(v) ? v.length : !!v; });
  };

  /* Simple = losslessly reconstructable from handle+variant alone. */
  const isSimple = (it) => {
    if (!it || it.quantity !== 1 || it.gift_card === true) return false;
    if (it.selling_plan_allocation || it.selling_plan || it.selling_plan_id) return false;
    if (hasComponents(it)) return false;
    const p = it.properties;
    if (p && typeof p !== 'object') return false;
    if (p && Object.keys(p).length) return false;
    return true;
  };

  const authorize = (cart, key, h, vid) => {
    const byKey = cart.items.filter((x) => x && x.key === key);
    const byId = matches(cart, h, vid);
    return byKey.length === 1 && byId.length === 1 && byKey[0] === byId[0] && isSimple(byKey[0]) ? byKey[0] : null;
  };

  const reAddable = async (h, vid) => {
    try {
      const r = await fetch(url('/products/' + h + '.js'), { headers: { Accept: 'application/json' } });
      const p = r.ok ? await r.json() : null;
      if (!p || !policyAllows(p.id) || !Array.isArray(p.variants)) return false;
      let v = p.variants.find((x) => String(x.id) === String(vid)) || null;
      if (!v && !vid && p.variants.length === 1) v = p.variants[0];
      return !!(v && v.available && v.requires_selling_plan !== true && p.requires_selling_plan !== true);
    } catch (e) { return false; }
  };

  /* ---- Dawn section rendering after the exact-key mutation ---- */
  const ctxFor = (row) => {
    const host = row.closest?.('cart-items, cart-drawer-items') || null;
    let sections = [];
    if (host && typeof host.getSectionsToRender === 'function') {
      try {
        (host.getSectionsToRender() || []).forEach((s) => {
          if (!s?.id || !s.section) return;
          if (sections.some((x) => x.section === s.section)) return;
          sections.push({ id: s.id, section: s.section, selector: s.selector || '' });
        });
      } catch (e) { sections = []; }
    }
    return { host, tagName: host ? host.tagName : '', sections };
  };

  const busyNodes = (ctx) => {
    const host = ctx.tagName ? document.querySelector(ctx.tagName.toLowerCase()) : null;
    const surface = ctx.tagName === 'CART-DRAWER-ITEMS'
      ? document.getElementById('CartDrawer-CartItems')
      : document.getElementById('main-cart-items');
    return [host || ctx.host, surface].filter((n, i, l) => !!n && l.indexOf(n) === i);
  };

  const setBusy = (ctx, busy) => {
    window.AnconaCartPending?.set(ctx, busy);
    busyNodes(ctx).forEach((n) => {
      n.classList.toggle('cart__items--disabled', busy);
      busy ? n.setAttribute('aria-busy', 'true') : n.removeAttribute('aria-busy');
    });
  };

  const isBusy = (ctx) => window.AnconaCartPending?.busy || busyNodes(ctx).some((n) =>
    n.classList.contains('cart__items--disabled') || n.getAttribute('aria-busy') === 'true');

  const reloadCart = (ctx) => {
    setBusy(ctx, true);
    setTimeout(() => window.location.assign(url('/cart')), 350);
    return 'reload';
  };

  const render = (state, ctx) => {
    if (!state || !Array.isArray(state.items) || !state.sections || !ctx.sections.length) return reloadCart(ctx);
    let ok = true;
    ctx.sections.forEach((s) => {
      try {
        const node = document.getElementById(s.id);
        const html = state.sections[s.section];
        if (!node || typeof html !== 'string') { ok = false; return; }
        const src = new DOMParser().parseFromString(html, 'text/html').querySelector(s.selector || '.shopify-section');
        if (!src) { ok = false; return; }
        ((s.selector && node.querySelector(s.selector)) || node).innerHTML = src.innerHTML;
      } catch (e) { ok = false; }
    });
    const empty = state.item_count === 0;
    ['cart-items', 'cart-drawer', '#main-cart-footer'].forEach((sel) => {
      document.querySelector(sel)?.classList.toggle('is-empty', empty);
    });
    window.AnconaCartPending?.sync();
    return ok ? 'sections' : reloadCart(ctx);
  };

  const publishState = (state) => {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        /* Our mutation response has already rendered Dawn's requested
           sections. Use Dawn's own cart-items source token so CartItems does
           not launch a redundant async self-refresh that can detach focus. */
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
      }
    } catch (e) {}
  };

  const removeExact = async (key, ctx) => {
    const body = { id: key, quantity: 0 };
    if (ctx.sections.length) {
      body.sections = ctx.sections.map((s) => s.section);
      body.sections_url = window.location.pathname;
    }
    const r = await fetch(url('/cart/change.js'), {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });
    if (!r.ok) throw new Error('remove-failed');
    const state = await r.json();
    if (!state || !Array.isArray(state.items) || state.errors) throw new Error('remove-invalid');
    if (state.items.some((x) => x && x.key === key)) throw new Error('remove-present');
    return state;
  };

  const confirmGone = async (key) => {
    await sleep(250);
    for (let i = 0; i < 8; i += 1) {
      try {
        const c = await readCart();
        if (!c.items.some((x) => x && x.key === key)) return true;
      } catch (e) {}
      await sleep(300);
    }
    return false;
  };

  const refocus = (ctx) => setTimeout(() => {
    try {
      const drawer = document.querySelector('cart-drawer');
      const fromDrawer = ctx.tagName === 'CART-DRAWER-ITEMS';
      if (fromDrawer !== !!drawer?.classList.contains('active')) return;
      const c = (ctx.tagName && document.querySelector(ctx.tagName.toLowerCase())) || document;
      const ae = document.activeElement;
      if (ae && ae !== document.body && c.contains?.(ae)) return;
      const next = c.querySelector('[data-av5-cart-move]') ||
        c.querySelector('cart-remove-button a, cart-remove-button button') ||
        (fromDrawer ? drawer.querySelector('.drawer__close, #CartDrawer-Checkout') : c.querySelector('.cart__empty-text')) ||
        (!fromDrawer && document.querySelector('#main-cart-footer [name="checkout"]')) ||
        c.querySelector('a[href]');
      if (next?.classList?.contains('cart__empty-text')) next.setAttribute('tabindex', '-1');
      next?.focus?.();
    } catch (e) {}
  }, 80);

  /* ---- Move flow (serialized: one move at a time) ---- */
  let moving = false;

  const identityOf = (row) => {
    const href = (row.querySelector('.cart-item__name') || row.querySelector('a[href*="/products/"]'))?.getAttribute('href') || '';
    const hm = /\/products\/([a-z0-9][a-z0-9-]*)/i.exec(href);
    const vm = /[?&]variant=(\d+)/.exec(href);
    const node = row.querySelector('[data-quantity-variant-id], [data-variant-id]');
    const d = node ? node.getAttribute('data-quantity-variant-id') || node.getAttribute('data-variant-id') || '' : '';
    return {
      h: hm ? hm[1] : '',
      pid: cleanProductId(row.dataset.av5ProductId),
      vid: String(d || (vm ? vm[1] : '')).replace(/\D/g, '')
    };
  };

  const onMove = async (btn) => {
    if (moving) return;
    const row = btn.closest('.cart-item');
    if (!row) return;
    const id = identityOf(row);
    if (!id.h || !id.vid || !policyAllows(id.pid)) { say(S.noIdentity); return; }
    const ctx = ctxFor(row);
    if (isBusy(ctx)) { say(S.busy); return; }
    moving = true;
    btn.dataset.busy = '1';
    setBusy(ctx, true);
    let savedOk = false, sent = false, state = null, reloading = false;
    try {
      const cart = await readCart();
      const m = matches(cart, id.h, id.vid);
      if (m.length !== 1 || !m[0] || typeof m[0].key !== 'string') throw new Error('drift');
      const it = m[0];
      if (cleanProductId(it.product_id) !== id.pid || !policyAllows(it.product_id)) throw new Error('drift');
      const key = it.key;
      /* SAVE FIRST — no removal request until the save is confirmed. */
      if (!await saveToBook(entryFor(it))) throw new Error('save-failed');
      savedOk = true;
      let outcome;
      if (!isSimple(it)) outcome = 'kept-complex';
      else if (!await reAddable(id.h, id.vid)) outcome = 'kept-unverified';
      else if (!authorize(await readCart(), key, id.h, id.vid)) outcome = 'kept-changed';
      else {
        sent = true;
        state = await removeExact(key, ctx);
        reloading = render(state, ctx) === 'reload';
        if (!reloading) {
          if (typeof ctx.host?.refreshOtherCartSurface !== 'function') throw new Error('cart-sync-unavailable');
          await ctx.host.refreshOtherCartSurface(state);
          init();
          setBusy(ctx, true);
        }
        outcome = (await confirmGone(key)) ? 'moved' : 'moved-review';
      }
      moving = false;
      btn.dataset.busy = '';
      if (!reloading && (outcome === 'moved' || outcome === 'moved-review')) publishState(state);
      if (!reloading) setBusy(ctx, false);
      if (outcome === 'moved') { say(S.moved); refocus(ctx); }
      else if (outcome === 'moved-review') { say(S.movedReview); refocus(ctx); }
      else if (outcome === 'kept-complex') say(S.keptComplex);
      else if (outcome === 'kept-unverified') say(S.keptUnverified);
      else say(S.keptChanged);
    } catch (err) {
      moving = false;
      btn.dataset.busy = '';
      if (sent) { reloading = reloadCart(ctx) === 'reload'; say(S.removalUnconfirmed); return; }
      setBusy(ctx, false);
      if (savedOk) say(S.keptChanged);
      else if (err?.message === 'save-failed') say(S.saveFailed);
      else if (err?.message === 'drift') say(S.noIdentity);
      else say(S.cartFailed);
    }
  };

  /* One delegated document listener for every current and future move button. */
  document.addEventListener('click', (ev) => {
    const btn = ev.target?.closest?.('[data-av5-cart-move]');
    if (btn) onMove(btn);
  });

  /* ---- Button injection (controls exist only when the flow is functional) ---- */
  const HEART = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true" focusable="false"><path d="M12 21s-7-4.35-9.5-8.5C1 9 2.5 5.5 6 5.5c2 0 3.2 1.2 4 2.3.8-1.1 2-2.3 4-2.3 3.5 0 5 3.5 3.5 7C19 16.65 12 21 12 21z"/></svg>';

  const inject = (container) => {
    container.querySelectorAll('.cart-item').forEach((row) => {
      if (row.dataset.av5MoveWired) return;
      if (!row.querySelector('cart-remove-button[data-index]')) return;
      if (!policyAllows(row.dataset.av5ProductId)) return;
      row.dataset.av5MoveWired = '1';
      const host = row.querySelector('.cart-item__quantity') || row.querySelector('.cart-item__details') || row;
      const name = (row.querySelector('.cart-item__name')?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ancona-v5-cart-move';
      btn.setAttribute('data-av5-cart-move', '');
      btn.setAttribute('aria-label', name ? S.movePrefix + name : S.move);
      btn.innerHTML = HEART + '<span aria-hidden="true">' + S.move + '</span>';
      host.appendChild(btn);
    });
  };

  /* Scoped observer per stable cart surface. Dawn may replace the entire
     <cart-drawer-items> node after pub/sub updates, so the drawer observer
     lives on its stable <cart-drawer> ancestor and survives that replacement.
     Never use a document-wide observer. */
  const observe = (container) => {
    if (!container || container.__av5MoveObs) return;
    container.__av5MoveObs = true;
    let mo = null;
    const run = () => {
      if (mo) mo.disconnect();
      try { inject(container); } catch (e) {}
      if (mo) { try { mo.observe(container, { childList: true, subtree: true }); } catch (e) {} }
    };
    try { mo = new MutationObserver(run); } catch (e) { mo = null; }
    run();
  };

  const init = () => {
    const drawer = document.querySelector('cart-drawer') ||
      document.getElementById('CartDrawer-CartItems') ||
      document.querySelector('.drawer__inner');
    const page = document.querySelector('cart-items') ||
      document.getElementById('main-cart-items');
    if (drawer) observe(drawer);
    if (page) observe(page);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', () => init());
})();
